import Link from 'next/link'
import type { ComponentPropsWithoutRef, ReactNode } from 'react'
import { cn } from '@/components/ui'

/**
 * Le filtre 2027 : la pastille au trait des filtres du site — blanc, contour
 * prune, découpe crème, inclinaison de 2°. Prune quand il est actif.
 *
 * Deux formes, le même dessin : `Filtre` (un bouton, pour les écrans clients
 * qui filtrent sur place) et `FiltreLien` (un lien, quand le filtre est porté
 * par l'adresse). `nombre` s'affiche en petit après le libellé.
 */
const CLASSES = cn(
  'inline-flex min-h-10 items-center rounded-pill px-4 py-[9px] text-[14px] font-extrabold tracking-[-0.01em] whitespace-nowrap',
  'shadow-[inset_0_0_0_2px_var(--ink),0_0_0_4px_var(--creme),0_4px_8px_rgba(43,23,72,0.14)]',
  'transition-[transform,background-color] duration-(--dur-base) ease-(--ease-umiros)',
  'hover:translate-y-[-2px] hover:rotate-0',
)

function contenu(children: ReactNode, nombre?: number) {
  return (
    <>
      {children}
      {nombre !== undefined && <small className="ml-1 text-[12px] font-semibold opacity-75">{nombre}</small>}
    </>
  )
}

export function Filtre({
  actif,
  nombre,
  incline = 'gauche',
  children,
  className,
  ...rest
}: {
  actif: boolean
  nombre?: number
  incline?: 'gauche' | 'droite'
  children: ReactNode
  className?: string
} & Omit<ComponentPropsWithoutRef<'button'>, 'children' | 'className'>) {
  return (
    <button
      type="button"
      aria-pressed={actif}
      className={cn(
        CLASSES,
        actif ? 'bg-ink text-surface' : 'bg-surface text-ink hover:bg-rose',
        incline === 'gauche' ? '-rotate-2' : 'rotate-2',
        className,
      )}
      {...rest}
    >
      {contenu(children, nombre)}
    </button>
  )
}

export function FiltreLien({
  actif,
  nombre,
  incline = 'gauche',
  children,
  className,
  ...rest
}: {
  actif: boolean
  nombre?: number
  incline?: 'gauche' | 'droite'
  children: ReactNode
  className?: string
} & Omit<ComponentPropsWithoutRef<typeof Link>, 'children' | 'className'>) {
  return (
    <Link
      aria-current={actif ? 'true' : undefined}
      className={cn(
        CLASSES,
        actif ? 'bg-ink text-surface' : 'bg-surface text-ink hover:bg-rose',
        incline === 'gauche' ? '-rotate-2' : 'rotate-2',
        className,
      )}
      {...rest}
    >
      {contenu(children, nombre)}
    </Link>
  )
}
